import { Box, Button, RadioInput } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { ErrorMessage, Form, Formik, FormikConfig, FormikProps } from "formik";
import * as React from "react";
import { CompactPicker } from "react-color";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { object, string } from "yup";

import style from "./report-template-form.scss";
import Info from "components/icons/Info";
import {
    getErasureSectionsKeys,
    getErasureSectionsTestIds,
    getHardwareSectionsKeys,
    getHardwareSectionsTestIds,
    getSectionKeys,
    getSectionKeysTestIds,
} from "components/reports/report-template/common";
import ReportTemplatePreviewModal from "components/reports/report-template/ReportTemplatePreviewModal";
import Tooltip from "components/tooltip/Tooltip";
import { ReportSection, reportSections, ReportTemplate, ReportType, TemplateTableData } from "domain/reports";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { StoreState } from "store";
import form from "styles/form.scss";
import { resolveColor } from "utils/commonFunctions";
import { useOutsideClick } from "utils/useOutsideClick";

import testIds from "testIds.json";

interface State {
    templateName: string;
    reportType: ReportType;
    logoVisible: boolean;
    disclaimer: string;
    color: string;
    disclaimerVisible: boolean;
}

const connector = connect((state: StoreState) => ({
    theme: state.themeReducer.theme,
}));

const ReportTemplateForm = (
    props: {
        submitEventHandler: (values: ReportTemplate) => void;
        onShowModal: (doShow: boolean) => void;
        templates: TemplateTableData[];
        template?: ReportTemplate | undefined | null;
        hasDynamicSize: (initial: boolean) => void;
    } & ConnectedProps<typeof connector>
): JSX.Element => {
    const { t } = useTranslation();
    const maxNameLength = 128;
    const maxDisclaimerLength = 1000;
    const allowedHtmlTags = ["ol", "ul", "li", "b", "p", "br", "strong", "u", "i"];
    const htmlTagRegex = /<\/?\s*([a-zA-Z0-9]+)[^>]*>/g;
    const [templateState, setTemplateState] = React.useState<ReportTemplate>({
        disclaimer: "",
        logoVisible: false,
        templateName: "",
        reportType: ReportType.STANDARD,
        uuid: "",
        sections: [...(props.template ? props.template.sections : reportSections)],
    });
    const sectionsKeys: Map<string, string> = getSectionKeys();
    const sectionKeysTestIds = getSectionKeysTestIds();
    const [previewCounter, setPreviewCounter] = React.useState(0);
    const [disclaimerAdded, setDisclaimerAdded] = React.useState(false);
    const [disclaimerValid, setDisclaimerValid] = React.useState(true);
    const [colorPickerVisible, setColorPickerVisible] = React.useState(false);
    const rawColor = props.theme.colorPickerBlack;
    const defaultColor = resolveColor(rawColor.replace(/^var\((.+)\)$/, "$1")) || rawColor;
    const ref = useOutsideClick(() => setColorPickerVisible(false));
    const hardwareSections: ReportSection[] = [
        "BATTERY_INFORMATION",
        "DEVICE_COMPONENTS",
        "HARDWARE_FUNCTIONAL_CHECKS",
    ];
    const erasureSections: ReportSection[] = ["ERASURE_INFORMATION", "ERASURE_VERIFICATION"];
    const hardwareSectionTranslationKeys = getHardwareSectionsKeys();
    const hardwareSectionTestIds = getHardwareSectionsTestIds();
    const erasureSectionTranslationKeys = getErasureSectionsKeys();
    const erasureSectionTestIds = getErasureSectionsTestIds();
    const [hardwareSectionsVisible, setHardwareSectionsVisible] = React.useState(false);
    const [erasureSectionsVisible, setErasureSectionsVisible] = React.useState(false);
    const [selectedSections, setSelectedSections] = React.useState(templateState.sections);
    const submitHandler: FormikConfig<State>["onSubmit"] = (values: State) => {
        usageStatisticsService.sendEvent({
            category: Category.REPORT_TEMPLATE,
            action: Action.ADD_REPORT_TEMPLATE,
        });
        props.hasDynamicSize(true);
        props.submitEventHandler({
            uuid: props.template?.uuid || "",
            sections: templateState.sections,
            templateName: values.templateName.trim(),
            reportType: values.reportType,
            disclaimer: values.disclaimerVisible ? values.disclaimer : "",
            logoVisible: values.logoVisible,
            version: props.template?.version,
            disclaimerColor: values.disclaimerVisible ? values.color : "",
        });
    };

    const createLabel = (
        id: string,
        text: string | undefined,
        errorArgument?: { [p: string]: string | undefined }
    ): JSX.Element => {
        return (
            <label htmlFor={id} className={classNames(form.label, errorArgument)}>
                {text}
            </label>
        );
    };

    const handleSectionToggle = (section: ReportSection, checked: boolean) => {
        if (checked) {
            if (section === "HARDWARE_DETAILS" && selectedSections.includes(section)) {
                setTemplateState((prevState) => ({
                    ...prevState,
                    sections: selectedSections,
                }));
                setHardwareSectionsVisible(checked);
            } else if (section === "ERASURE_RESULT" && selectedSections.includes(section)) {
                setTemplateState((prevState) => ({
                    ...prevState,
                    sections: selectedSections,
                }));
                setErasureSectionsVisible(checked);
            } else {
                setTemplateState((prevState) => ({
                    ...prevState,
                    sections: [...prevState.sections, section],
                }));
            }
        } else {
            if (section === "HARDWARE_DETAILS") {
                const filteredList: ReportSection[] = [];
                templateState.sections.forEach((section) => {
                    if (!hardwareSections.includes(section) && section != "HARDWARE_DETAILS") {
                        filteredList.push(section);
                    }
                });
                setSelectedSections(templateState.sections);
                setTemplateState((prevState) => ({
                    ...prevState,
                    sections: filteredList,
                }));
                setHardwareSectionsVisible(checked);
            } else if (section === "ERASURE_RESULT") {
                const filteredList: ReportSection[] = [];
                templateState.sections.forEach((section) => {
                    if (!erasureSections.includes(section) && section != "ERASURE_RESULT") {
                        filteredList.push(section);
                    }
                });
                setSelectedSections(templateState.sections);
                setTemplateState((prevState) => ({
                    ...prevState,
                    sections: filteredList,
                }));
                setErasureSectionsVisible(checked);
            } else {
                setTemplateState((prevState) => ({
                    ...prevState,
                    sections: prevState.sections.filter((each) => each !== section),
                }));
            }
        }
        updatePreview();
    };

    const createSectionCheck = (
        section: ReportSection,
        key: number,
        translationKeys: Map<string, string>,
        keyTestIds: Map<ReportSection, string>
    ): JSX.Element => {
        return (
            <div className={style.item} key={key}>
                <label className={form.container}>
                    <input
                        type={"checkbox"}
                        name={section}
                        className={form.input}
                        defaultChecked={props.template ? props.template.sections.includes(section) : true}
                        onChange={(event) => handleSectionToggle(section, event.target.checked)}
                        data-testid={keyTestIds.get(section)}
                    />
                    <span className={form.checkmark} />
                </label>
                {createLabel(section, translationKeys.get(section))}
            </div>
        );
    };

    const createHardwareSectionCheck = (section: ReportSection, key: number): JSX.Element => {
        return (
            <div>
                {createSectionCheck(section, key, sectionsKeys, sectionKeysTestIds)}
                <div
                    hidden={!hardwareSectionsVisible && !templateState.sections.includes("HARDWARE_DETAILS")}
                    className={style.indentedField}
                >
                    {hardwareSections.map((each, hardwareSectionKey) => {
                        return createSectionCheck(
                            each,
                            hardwareSectionKey,
                            hardwareSectionTranslationKeys,
                            hardwareSectionTestIds
                        );
                    })}
                </div>
            </div>
        );
    };

    const createErasureSectionCheck = (section: ReportSection, key: number): JSX.Element => {
        return (
            <div>
                {createSectionCheck(section, key, sectionsKeys, sectionKeysTestIds)}
                <div
                    hidden={!erasureSectionsVisible && !templateState.sections.includes("ERASURE_RESULT")}
                    className={style.indentedField}
                >
                    {erasureSections.map((each, erasureSectionKey) => {
                        return createSectionCheck(
                            each,
                            erasureSectionKey,
                            erasureSectionTranslationKeys,
                            erasureSectionTestIds
                        );
                    })}
                </div>
            </div>
        );
    };

    const options = [
        {
            label: t("ReportTemplate.form.standard"),
            value: ReportType.STANDARD,
        },
        {
            label: t("ReportTemplate.form.advanced"),
            value: ReportType.ADVANCED,
        },
    ];

    function handleHideModal() {
        props.onShowModal(false);
        props.hasDynamicSize(true);
    }
    function isTemplateNameAvailable(templateName: string | undefined | null): boolean {
        return (
            typeof props.templates.find(
                (item) =>
                    item.templateName.trim() === templateName?.trim() && item.uuid !== (props.template?.uuid || "")
            ) === "undefined"
        );
    }

    const validateDisclaimer = (
        value: string | null | undefined,
        context?: { disclaimerVisible?: boolean }
    ): boolean => {
        if (!context?.disclaimerVisible || !value) {
            return true;
        }

        const matches = [...value.matchAll(htmlTagRegex)];
        const invalidTags = matches
            .map((match) => match[1].toLowerCase())
            .filter((tag) => !allowedHtmlTags.includes(tag));

        return invalidTags.length === 0;
    };

    const updatePreview = () => {
        if (!disclaimerValid) {
            return;
        }
        setPreviewCounter((prevState) => prevState + 1);
        setDisclaimerAdded(false);
    };

    const hasDisclaimer = () => {
        return props.template ? props.template.disclaimer !== null && props.template.disclaimer !== "" : false;
    };

    return (
        <div className={style.templateContainer}>
            <Formik
                initialValues={{
                    templateName: props.template ? props.template.templateName : "",
                    reportType: props.template ? props.template.reportType : ReportType.STANDARD,
                    logoVisible: props.template ? props.template.logoVisible : true,
                    disclaimer:
                        props.template === undefined ? "" : props.template?.disclaimer ? props.template.disclaimer : "",
                    color: !props.template
                        ? defaultColor
                        : props.template?.disclaimerColor
                          ? props.template.disclaimerColor
                          : defaultColor,
                    disclaimerVisible: hasDisclaimer(),
                }}
                onSubmit={submitHandler}
                validationSchema={object().shape({
                    templateName: string()
                        .required(t("ReportTemplate.form.name.required"))
                        .max(maxNameLength, t("ReportTemplate.form.name.lengthException", { length: maxNameLength }))
                        .test("Name already exists", t("ReportTemplate.form.name.alreadyExistsException"), (value) =>
                            isTemplateNameAvailable(value)
                        )
                        .test("HTML tags not allowed", t("ReportTemplate.form.name.htmlTagsNotAllowed"), (value) => {
                            const htmlTagRegex = /<\/?\s*([a-zA-Z0-9]+)[^>]*>/g;
                            return !value || !htmlTagRegex.test(value);
                        }),
                    disclaimer: string().test(
                        "Invalid HTML tags",
                        t("ReportTemplate.form.disclaimer.invalidHtmlTags"),
                        function (value) {
                            return validateDisclaimer(value, {
                                disclaimerVisible: this.parent.disclaimerVisible,
                            });
                        }
                    ),
                })}
                validateOnChange={true}
            >
                {({ values, errors, handleChange }: FormikProps<State>) => {
                    return (
                        <Form className={style.templateContainer}>
                            <div className={style.form}>
                                <div className={classNames(style.gridItem, style.formGridItem)}>
                                    <div className={form.formFields}>
                                        {templateState.templateName}
                                        {createLabel("templateName", t("ReportTemplate.form.name.label"), {
                                            [style.nameLabel]: "true",
                                        })}
                                        <input
                                            autoFocus
                                            id={"templateName"}
                                            type={"text"}
                                            className={classNames(
                                                form.input,
                                                form.fixedWidthInput,
                                                style.inputWidth,
                                                style.nameInput,
                                                {
                                                    [form.inputError]: errors.templateName,
                                                }
                                            )}
                                            onChange={handleChange}
                                            value={values.templateName}
                                            data-testid={
                                                testIds.workArea.report.erasure.reportTemplate.manageTemplateDialog
                                                    .nameInput.itself
                                            }
                                        />
                                        <div
                                            className={classNames(form.error, style.nameError)}
                                            data-testid={
                                                testIds.workArea.report.erasure.reportTemplate.manageTemplateDialog
                                                    .nameInput.errorLabel
                                            }
                                        >
                                            <ErrorMessage name={"templateName"} />
                                        </div>
                                    </div>
                                    <Box marginTop={"Md"} verticalAlignment={"Top"} className={style.radioFormFields}>
                                        {createLabel("reports", t("ReportTemplate.form.reportType"))}
                                        <Box columnLayout>
                                            {options.map((option, index) => (
                                                <Box key={index} gap={"Xxs"} verticalAlignment={"Center"}>
                                                    <RadioInput
                                                        id={`reportType-${option.value}`}
                                                        name={"reportType"}
                                                        value={option.value}
                                                        label={option.label}
                                                        checked={values.reportType === option.value}
                                                        onChange={(event) => {
                                                            handleChange(event);
                                                            updatePreview();
                                                        }}
                                                        data-testid={
                                                            testIds.workArea.report.erasure.reportTemplate
                                                                .manageTemplateDialog.reportTypeSelect
                                                        }
                                                        dataType={option.value}
                                                        hideLabelSection
                                                    />
                                                    {option.value === ReportType.ADVANCED && (
                                                        <Tooltip
                                                            allowHTML={true}
                                                            interactive={true}
                                                            placement={"right"}
                                                            content={t("ReportTemplate.form.reportInfo")}
                                                        >
                                                            <div
                                                                className={classNames(
                                                                    form.infoIcon,
                                                                    style.radioInfoIcon
                                                                )}
                                                                tabIndex={0}
                                                            >
                                                                <Info
                                                                    borderColor={props.theme.contentBackgroundColor}
                                                                    color={props.theme.iconFillColor}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                        <div className={form.error}>
                                            <ErrorMessage name="reports" />
                                        </div>
                                    </Box>

                                    <div className={form.formFields}>
                                        <div className={form.formFieldsColumnDirection}>
                                            <label>{t("ReportTemplate.form.introduction")}</label>
                                            <div className={style.item}>
                                                <label className={form.container}>
                                                    <input
                                                        className={form.input}
                                                        id={"logoVisible"}
                                                        type={"checkbox"}
                                                        onChange={(event) => {
                                                            handleChange(event);
                                                            updatePreview();
                                                        }}
                                                        checked={values.logoVisible}
                                                        data-testid={
                                                            testIds.workArea.report.erasure.reportTemplate
                                                                .manageTemplateDialog.companyLogoCheckbox
                                                        }
                                                    />
                                                    <span className={form.checkmark} />
                                                </label>
                                                {createLabel("logoVisible", t("ReportTemplate.form.logo.label"))}
                                                <Tooltip
                                                    allowHTML={true}
                                                    interactive={true}
                                                    placement={"right"}
                                                    content={t("ReportTemplate.form.logo.info")}
                                                >
                                                    <div
                                                        className={classNames(form.infoIcon, style.logoInfoIcon)}
                                                        tabIndex={0}
                                                    >
                                                        <Info
                                                            borderColor={props.theme.contentBackgroundColor}
                                                            color={props.theme.iconFillColor}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </div>
                                            <div className={classNames(style.item, style.disclaimerItem)}>
                                                <label className={form.container}>
                                                    <input
                                                        className={form.input}
                                                        id={"disclaimerVisible"}
                                                        type={"checkbox"}
                                                        onChange={(event) => {
                                                            const isChecked = event.target.checked;
                                                            handleChange(event);
                                                            if (!isChecked) {
                                                                setDisclaimerValid(true);
                                                                updatePreview();
                                                                return;
                                                            }

                                                            const isValid = validateDisclaimer(values.disclaimer, {
                                                                disclaimerVisible: true,
                                                            });

                                                            setDisclaimerValid(isValid);

                                                            if (isValid) {
                                                                updatePreview();
                                                            }
                                                        }}
                                                        checked={values.disclaimerVisible}
                                                        data-testid={
                                                            testIds.workArea.report.erasure.reportTemplate
                                                                .manageTemplateDialog.disclaimerCheckbox
                                                        }
                                                    />
                                                    <span className={form.checkmark} />
                                                </label>
                                                {createLabel(
                                                    "disclaimerVisible",
                                                    t("ReportTemplate.form.disclaimer.label")
                                                )}
                                                <Tooltip
                                                    allowHTML={true}
                                                    interactive={true}
                                                    placement={"right"}
                                                    content={t("ReportTemplate.form.disclaimer.tooltip")}
                                                >
                                                    <div
                                                        className={classNames(form.infoIcon, style.disclaimerInfoIcon)}
                                                        tabIndex={0}
                                                    >
                                                        <Info
                                                            borderColor={props.theme.contentBackgroundColor}
                                                            color={props.theme.iconFillColor}
                                                        />
                                                    </div>
                                                </Tooltip>
                                                <div hidden={!values.disclaimerVisible} className={style.indentedField}>
                                                    <div
                                                        key={"disclaimerText"}
                                                        className={classNames(
                                                            form.formFields,
                                                            style.item,
                                                            style.disclaimerItem
                                                        )}
                                                    >
                                                        <div className={form.notes}>
                                                            <textarea
                                                                id={"disclaimer"}
                                                                className={classNames(
                                                                    form.input,
                                                                    style.disclaimerTextArea,
                                                                    {
                                                                        [form.inputError]: errors.disclaimer,
                                                                    }
                                                                )}
                                                                maxLength={maxDisclaimerLength}
                                                                onChange={(event) => {
                                                                    handleChange(event);
                                                                    setDisclaimerAdded(true);
                                                                    const isValid = validateDisclaimer(
                                                                        event.target.value,
                                                                        {
                                                                            disclaimerVisible: values.disclaimerVisible,
                                                                        }
                                                                    );
                                                                    setDisclaimerValid(isValid);
                                                                }}
                                                                onMouseLeave={() => {
                                                                    if (disclaimerAdded) {
                                                                        updatePreview();
                                                                    }
                                                                }}
                                                                value={values.disclaimer}
                                                                data-testid={
                                                                    testIds.workArea.report.erasure.reportTemplate
                                                                        .manageTemplateDialog.disclaimerTextArea.itself
                                                                }
                                                            />
                                                            {!errors.disclaimer && (
                                                                <span className={form.optional}>
                                                                    {t(
                                                                        "ReportTemplate.form.disclaimer.charactersCount",
                                                                        {
                                                                            charactersLeft:
                                                                                maxDisclaimerLength -
                                                                                values.disclaimer.length,
                                                                            total: maxDisclaimerLength,
                                                                        }
                                                                    )}
                                                                </span>
                                                            )}
                                                            <span
                                                                className={classNames(
                                                                    form.error,
                                                                    style.disclaimerError
                                                                )}
                                                                data-testid={
                                                                    testIds.workArea.report.erasure.reportTemplate
                                                                        .manageTemplateDialog.disclaimerTextArea
                                                                        .errorLabel
                                                                }
                                                            >
                                                                <ErrorMessage name={"disclaimer"} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div
                                                        key={"disclaimerColor"}
                                                        className={classNames(
                                                            form.formFields,
                                                            style.item,
                                                            style.disclaimerItem,
                                                            style.disclaimerColorLabel
                                                        )}
                                                    >
                                                        {createLabel(
                                                            "color",
                                                            t("ReportTemplate.form.disclaimer.colorPicker")
                                                        )}
                                                        <div
                                                            id={"color"}
                                                            ref={ref}
                                                            className={classNames(
                                                                style.border,
                                                                style.disclaimerColorBlock
                                                            )}
                                                        >
                                                            <div
                                                                onClick={() =>
                                                                    setColorPickerVisible(!colorPickerVisible)
                                                                }
                                                            >
                                                                <div
                                                                    className={style.colorBox}
                                                                    style={{ backgroundColor: values.color }}
                                                                ></div>
                                                                <div
                                                                    className={classNames(
                                                                        style.selectArrow,
                                                                        style.arrowForColorPicker
                                                                    )}
                                                                />
                                                            </div>
                                                            <div
                                                                hidden={!colorPickerVisible}
                                                                className={style.colorPicker}
                                                            >
                                                                <CompactPicker
                                                                    onChange={(color) => {
                                                                        values.color = color.hex;
                                                                        updatePreview();
                                                                    }}
                                                                    color={values.color}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={classNames(style.item, style.disclaimerItem)}>
                                                {reportSections.map((each, key) => {
                                                    if (
                                                        each !== "HARDWARE_DETAILS" &&
                                                        !hardwareSections.includes(each) &&
                                                        each !== "ERASURE_RESULT" &&
                                                        !erasureSections.includes(each)
                                                    ) {
                                                        return createSectionCheck(
                                                            each,
                                                            key,
                                                            sectionsKeys,
                                                            sectionKeysTestIds
                                                        );
                                                    } else if (each === "HARDWARE_DETAILS") {
                                                        return createHardwareSectionCheck(each, key);
                                                    } else if (each === "ERASURE_RESULT") {
                                                        return createErasureSectionCheck(each, key);
                                                    }
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className={style.previewContainer}
                                    data-testid={
                                        testIds.workArea.report.erasure.reportTemplate.manageTemplateDialog
                                            .previewIframe
                                    }
                                >
                                    <ReportTemplatePreviewModal
                                        previewCounter={previewCounter}
                                        templateDetails={{
                                            uuid: props.template?.uuid || "",
                                            sections: templateState.sections,
                                            templateName: values.templateName.trim(),
                                            reportType: values.reportType,
                                            disclaimer: values.disclaimerVisible ? values.disclaimer : "",
                                            logoVisible: values.logoVisible,
                                            disclaimerColor: values.color,
                                        }}
                                    />
                                </div>
                            </div>

                            <div className={classNames(form.buttonContainer, style.buttonContainer)}>
                                <Button
                                    type={"submit"}
                                    variant={"Primary"}
                                    disabled={!!errors.templateName || !!errors.disclaimer}
                                    testId={
                                        testIds.workArea.report.erasure.reportTemplate.manageTemplateDialog.addButton
                                    }
                                >
                                    {props.template ? t("Common.save") : t("Common.create")}
                                </Button>
                                <Button
                                    type={"button"}
                                    variant={"Secondary"}
                                    testId={testIds.common.dialog.closeButton}
                                    onClick={handleHideModal}
                                >
                                    {t("Common.cancel")}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default connector(ReportTemplateForm);
